import { getApiUrl } from "./utils/api";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import WritePoem from "./pages/WritePoem";
import Profile from "./pages/Profile";
import Navbar from "./components/Navbar";
import AppSidebar from "./components/AppSidebar";
import PoemDetail from "./pages/PoemDetail";
import UserProfile from "./pages/UserProfile";
import EditPoem from "./pages/EditPoem";
import AdminDashboard from "./pages/AdminDashboard";
import ModeratorDashboard from "./pages/ModeratorDashboard";
import Writers from "./pages/Writers";
import GroupDetail from "./pages/GroupDetail";
import Feedback from "./pages/Feedback"; // Import the new page
import Chat from "./pages/Chat";
import About from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import "./App.css";
import "./editorOverrides.css";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { UserContext } from "./context/UserContext"; // adjust path if needed
import { ChatProvider } from "./context/ChatContext";
import { ToastProvider, useToast } from "./context/ToastContext";
import ChatModal from "./components/ChatModal";
import ToastContainer from "./components/ToastContainer";
import CookieConsent from "./components/CookieConsent";
import SkipToContent from "./components/SkipToContent";
import ErrorBoundary from "./components/ErrorBoundary";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useSidebarScrollHeight } from "./hooks/useSidebarScrollHeight";

function LegacySearchRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q");
  return <Navigate to={q ? `/?q=${encodeURIComponent(q)}` : "/"} replace />;
}

const KNOWN_APP_ROUTES = [
  /^\/$/,
  /^\/poems$/,
  /^\/poems\/[^/]+$/,
  /^\/login$/,
  /^\/register$/,
  /^\/write$/,
  /^\/profile$/,
  /^\/feedback$/,
  /^\/chat$/,
  /^\/about$/,
  /^\/privacy$/,
  /^\/terms$/,
  /^\/users\/[^/]+$/,
  /^\/edit-poem\/[^/]+$/,
  /^\/admin\/?$/,
  /^\/moderator\/?$/,
  /^\/search$/,
  /^\/writers$/,
  /^\/groups\/[^/]+\/write$/,
  /^\/groups\/[^/]+$/,
  /^\/404$/,
];

function isKnownAppRoute(pathname) {
  return KNOWN_APP_ROUTES.some((pattern) => pattern.test(pathname));
}

// Component that accesses chat context
function AppContent() {
  const { user } = useContext(UserContext);
  const navbarRef = useRef();
  const { toasts, removeToast } = useToast();
  const location = useLocation();
  const pathname =
    location.pathname.length > 1
      ? location.pathname.replace(/\/+$/, "")
      : location.pathname;
  const isAdminRoute = /^\/admin(\/|$)/.test(pathname);
  const showNavbar = !isAdminRoute;
  const isFullWidthRoute = /^\/(admin|moderator)(\/|$)/.test(pathname);
  const isHomeRoute =
    pathname === "/" ||
    pathname === "/poems" ||
    pathname === "/writers" ||
    pathname === "/chat";
  const isNotFoundRoute = !isKnownAppRoute(pathname);
  const isProfileRoute =
    pathname === "/profile" || /^\/users\/[^/]+$/.test(pathname);
  const isWriteRoute =
    pathname === "/write" ||
    /^\/edit-poem\/[^/]+$/.test(pathname) ||
    /^\/groups\/[^/]+\/write$/.test(pathname);
  const isGroupDetailRoute = /^\/groups\/[^/]+$/.test(pathname);
  const isNoSidebarRoute =
    isNotFoundRoute ||
    isProfileRoute ||
    isWriteRoute ||
    ["/about", "/terms", "/privacy", "/feedback", "/404", "/login", "/register"].includes(
      pathname
    );
  const showAppSidebar = !isFullWidthRoute && !isNoSidebarRoute;
  useSidebarScrollHeight(showAppSidebar);
  const layoutClass = isFullWidthRoute
    ? "app-layout app-layout--full"
    : isHomeRoute
    ? "app-layout app-layout--home"
    : isNoSidebarRoute
    ? `app-layout app-layout--no-sidebar${
        isProfileRoute ? " app-layout--feed-aligned" : ""
      }`
    : isGroupDetailRoute
    ? "app-layout app-layout--group"
    : "app-layout";
  const scrollPanesClass = isFullWidthRoute ? "" : " app-layout--scroll-panes";

  useLayoutEffect(() => {
    const layout = document.querySelector(".app-layout");
    if (!layout || window.innerWidth < 960) return;

    if (isGroupDetailRoute || !isFullWidthRoute) {
      layout.style.setProperty("--sidebar-align-offset", "0px");
    }
  }, [pathname, isGroupDetailRoute, isFullWidthRoute]);

  return (
    <div
      className={`app-shell flex flex-col min-h-screen${
        isFullWidthRoute ? "" : " app-shell--panes"
      }`}
    >
      <SkipToContent />
      {showNavbar && <Navbar ref={navbarRef} />}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <main className="flex-grow max-w-full peer-home" role="main">
        <div className={`${layoutClass}${scrollPanesClass}`}>
          {showAppSidebar && (
            <div className="app-layout-sidebar">
              <AppSidebar />
            </div>
          )}
          <div
            id="main-content"
            className={isFullWidthRoute ? "app-layout-main app-layout-main--full" : "app-layout-main"}
          >
            <div
              className={
                isFullWidthRoute ? "app-layout-main-scroll app-layout-main-scroll--full" : "app-layout-main-scroll"
              }
            >
            <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/poems" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/write" element={<WritePoem />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/feedback" element={<Feedback />} />{" "}
          {/* Add the feedback route */}
          <Route path="/chat" element={<Chat />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/poems/:id"
            element={<PoemDetail navbarRef={navbarRef} />}
          />
          <Route path="/users/:username" element={<UserProfile />} />
          <Route path="/edit-poem/:id" element={<EditPoem />} />
          <Route
            path="/admin"
            element={
              user && (user.role === "admin" || user.role === "superadmin") ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/moderator"
            element={
              user && (user.role === "moderator" || user.role === "admin" || user.role === "superadmin") ? (
                <ModeratorDashboard />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/search" element={<LegacySearchRedirect />} />
          <Route path="/writers" element={<Writers />} />
          <Route path="/groups/:groupId/write" element={<WritePoem />} />
          <Route path="/groups/:id" element={<GroupDetail />} />
          {/* Explicit 404 route */}
          <Route path="/404" element={<NotFound />} />
          {/* Catch-all route for 404 errors */}
          <Route path="*" element={<NotFound />} />
            </Routes>
            </div>
          </div>
        </div>
      </main>
      <CookieConsent />
      <ChatModal />
    </div>
  );
}

function App() {
  const { user, setUser, userLoading } = useContext(UserContext);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (!userLoading) {
      setIsHydrated(true);
    }
  }, [userLoading]);

  // Global fetch wrapper to auto-logout on jwt expiry
  useEffect(() => {
    const originalFetch = window.fetch;
    let redirecting = false; // Prevent multiple redirects
    
    window.fetch = async (...args) => {
      try {
        const res = await originalFetch(...args);
        try {
          if (res.status === 401) {
            const data = await res
              .clone()
              .json()
              .catch(() => ({}));
            const msg = (data && (data.msg || data.error)) || "";
            // Only redirect if:
            // 1. User is actually logged in (has user in state)
            // 2. Message indicates JWT expired
            // 3. Not already on login page
            // 4. Not already redirecting
            if (
              user &&
              String(msg).toLowerCase().includes("jwt expired") &&
              !window.location.pathname.includes("/login") &&
              !redirecting
            ) {
              redirecting = true;
              localStorage.removeItem("user");
              setUser(null);
              // Use replace instead of href to prevent back button issues
              window.location.replace("/login");
            }
          }
        } catch {}
        return res;
      } catch (error) {
        // If the fetch itself fails (network error), just re-throw it
        throw error;
      }
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, [setUser, user]);

  // Lightweight online presence ping (avoid full profile refresh on every interaction)
  useEffect(() => {
    if (!user) return;

    let lastPingAt = 0;
    const PING_INTERVAL_MS = 120000;

    const pingActivity = async () => {
      try {
        const userLeaving = localStorage.getItem("userLeaving");
        if (userLeaving) {
          localStorage.removeItem("userLeaving");
          return;
        }

        if (window.location.pathname.includes("/login")) return;

        const now = Date.now();
        if (now - lastPingAt < PING_INTERVAL_MS) return;
        lastPingAt = now;

        await fetch(getApiUrl("/api/user/ping"), {
          method: "POST",
          credentials: "include",
        });
      } catch (error) {
        console.error("Activity ping failed:", error);
      }
    };

    const handleUserActivity = () => {
      pingActivity();
    };

    const events = ["mousedown", "keydown", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    const interval = setInterval(pingActivity, 300000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        lastPingAt = 0;
        pingActivity();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleBeforeUnload = () => {
      try {
        localStorage.setItem("userLeaving", Date.now().toString());
      } catch (error) {
        console.error("Beforeunload handler failed:", error);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user]);

  // Wait until hydration completes before rendering routes
  if (!isHydrated) return null;

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  
  // Debug: Log if client ID is missing (only in development)
  if (process.env.NODE_ENV === "development" && !googleClientId) {
    console.warn("⚠️ REACT_APP_GOOGLE_CLIENT_ID is not set in .env file");
  }

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={googleClientId}>
        <ToastProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ChatProvider>
              <AppContent />
            </ChatProvider>
          </Router>
        </ToastProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
